<script setup lang="ts">
// composables
import { useActiveSectionObserver } from '~/composables/useActiveSectionObserver';

// stores
import useUIStore from '~/stores/ui';

// types
import type { IContentTable, ContentTableNavItem } from '~/types';

type Item = Required<Omit<IContentTable, 'nested'>>;

type TableOfContentProps = {
  items: Item[];
};

type TableOfContentSlots = {
  title(): any;
  item(props: { item: Item; active: boolean }): any;
};

const props = withDefaults(defineProps<TableOfContentProps>(), {
  items: () => [],
});

defineSlots<TableOfContentSlots>();

const { headerOffset: offset } = storeToRefs(useUIStore());
const navItems = ref<ContentTableNavItem[]>([]);

watch(
  () => props.items,
  items => {
    items.forEach(item => {
      navItems.value.push({ ...item, offset });
    });
  },
  {
    flush: 'post',
    immediate: true,
  },
);

const { activeId } = useActiveSectionObserver(navItems, offset);
</script>

<template>
  <aside data-component-name="TableOfContent">
    <slot name="title" />

    <nav>
      <ul>
        <li v-for="item of items" :key="item.href">
          <slot :item :active="item.href === activeId" name="item">
            {{ item.title }}
          </slot>
        </li>
      </ul>
    </nav>
  </aside>
</template>

<style scoped lang="scss">
[data-component-name='TableOfContent'] {
  position: sticky;
  top: v-bind(" offset + 'px' ");
  transition: top 0.5s ease;
}
</style>
